import React, { FC, useCallback, useState } from 'react';

import { Col, Row } from '@components/formStyles';
import { useBreakpoint } from '@contexts/breakpoints';
import BorderedLabel from '@components/ui/labels/bordered/BorderedLabel';
import Alert, {
  AlertType,
} from '@components/views/hotel/detail/components/room-selection/components/Alert';
import { formatCurrency, getAvailableStatus, typeToName } from '@utils/utils';
import {
  C4CSalesItemType,
  HotelAvailabilityStatusType,
  LoyaltyType,
  RoomExpediaPriceDetailViewModel,
  RoomCancellationPolicyDetailViewModel,
} from '@api/index';
import { Currencies } from '@constants/constants';
import LetUsCallYou from '@components/ui/LetUsCallYou/LetUsCallYou';
import { DateType, formatMomentDate, GTM_DATE_FORMAT } from '@utils/dateUtil';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import {
  addHotelLetUsCallYouClickEventToGTM,
  addHotelLetUsCallYouSuccessEventToGTM,
  addHotelRoomSelectionClickEventToGTM,
} from '@utils/gtm/hotel-gtm';
import { useAuthState } from '@contexts/auth/AuthContext';
import {
  shouldHideLoyalty,
  requiredAuthentication,
  LoginRedirectModal,
  checkLoyaltyPreviouslyValidated,
} from '@components/ui/loyalty-verifications/helpers';
import { useToggle } from '@hooks/common';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import Price from '@components/ui/price';
import FeeDetails from '@components/ui/fee/Details/Details';
import { Discount } from '@components/ui/labels/discount/DiscountLabelStyle';
import { OrText } from '@uikit/milePriceBadge/styled';
import MilePriceBadge from '@uikit/milePriceBadge';
import { AlertIcon, InfoIcon } from '@assets/icons';

import {
  Wrapper,
  Title,
  Description,
  StyledButton,
  WarningWrapper,
  DynamicRow,
  PriceContainer,
  PeopleNightCountWrapper,
  PeopleNightCount,
} from './RoomSelectionCardStyle';
import { useSetModalLoyaltyType } from '../../context';
import ReadMoreDescription from './components/ReadMoreDescription';
import { StyledIconLabel } from '../room-information/DetailedRoomStyle';
import { cancellationPolicies } from '../room-information/DetailedRoom';

export interface RoomSelectionCardProps {
  id?: number;
  hotelName?: string;
  countryCode?: string;
  title?: string;
  promotionInfoName?: string;
  prices?: {
    originalPrice?: number;
    price?: number;
    currency?: string;
    originalCurrency?: string;
    discount?: string;
    milePrice?: number;
  };
  roomInformation?: string;
  status?: HotelAvailabilityStatusType;
  onSelectVariation?: () => void;
  isSelected?: boolean;
  loyalty?: {
    isActive?: boolean;
    loyaltyType?: LoyaltyType;
  };
  hasCancelletion?: boolean;
  warningText?: string;
  alertMessage?: string;
  openDatePicker?: () => void;
  formCheckInDate?: DateType | null;
  formCheckOutDate?: DateType | null;
  hotelRoomList?: Array<ListingSearchRoomInterface>;
  feeDetails?: RoomExpediaPriceDetailViewModel;
  isExpedia?: boolean;
  roomSquareMeter?: string;
  description?: string;
  cancellationPolicyDetails?: Array<RoomCancellationPolicyDetailViewModel>;
  childWithPriceCount?: number;
  isRefundable?: boolean;
}

interface ButtonActionType {
  text: string;
  function?: () => void;
}

const showPrice = (status: HotelAvailabilityStatusType): boolean => {
  switch (status) {
    case HotelAvailabilityStatusType.MAX_PEOPLE_EXCEEDED:
    case HotelAvailabilityStatusType.ACCOMMODATION_TOO_LONG:
    case HotelAvailabilityStatusType.RESTRICTED:
      return false;
    default:
      return true;
  }
};

const RoomSelectionCard: FC<RoomSelectionCardProps> = ({
  id,
  hotelName,
  countryCode,
  title,
  promotionInfoName,
  prices,
  roomInformation = '',
  status,
  onSelectVariation,
  isSelected = false,
  loyalty,
  warningText,
  alertMessage,
  formCheckInDate,
  formCheckOutDate,
  hotelRoomList,
  feeDetails,
  isExpedia,
  roomSquareMeter,
  description,
  openDatePicker,
  cancellationPolicyDetails,
  isRefundable,
}) => {
  const [isLetUsCallYouModalVisible, setIsLetUsCallYouModalVisible] =
    useState(false);
  const [showLoginRedirectionModal, , setShowLoginRedirectionModal] =
    useToggle();

  const setModalLoyaltyType = useSetModalLoyaltyType();
  const isPriceAvailable = useCallback(showPrice, [status]);

  const needConfirmation = loyalty?.loyaltyType && !loyalty?.isActive;
  const TooltipText = (props) => {
    const { cancellationPolicyDetails } = props;
    if (!cancellationPolicyDetails?.[0]?.description) {
      return null;
    }
    if (cancellationPolicyDetails?.length) {
      return cancellationPolicies(cancellationPolicyDetails)();
    }
    return null;
  };
  const alertType = getAvailableStatus(status).alertType;
  const { xl } = useBreakpoint();

  const handleSelectVariation = (): void => {
    onSelectVariation && onSelectVariation();
    addHotelRoomSelectionClickEventToGTM({
      title: title || '',
      hotelName: hotelName || '',
      checkInDate: formatMomentDate(formCheckInDate, GTM_DATE_FORMAT),
      checkOutDate: formatMomentDate(formCheckOutDate, GTM_DATE_FORMAT),
      adultCount: hotelRoomList?.[0]?.adultCount || 0,
      childCount: hotelRoomList?.[0]?.childrenList.length || 0,
      price: prices?.price || prices?.originalPrice || 0,
      serviceType: 'Hotel',
    });
  };

  const { loginInfo } = useAuthState();
  const buttonAction = (
    status?: HotelAvailabilityStatusType
  ): ButtonActionType => {
    if (isSelected) {
      return {
        text: 'Oda Seçildi',
      };
    } else if (needConfirmation) {
      return {
        text: 'Doğrula',
        function: () => {
          if (requiredAuthentication(loyalty?.loyaltyType) && !loginInfo) {
            setShowLoginRedirectionModal(true);
          } else {
            setModalLoyaltyType(loyalty?.loyaltyType || null);
          }
        },
      };
    } else if (status) {
      return {
        text: getAvailableStatus(status).button,
        function:
          status === HotelAvailabilityStatusType.AVAILABLE
            ? handleSelectVariation
            : openDatePicker,
      };
    } else {
      return {
        text: 'Tarih Seç',
        function: openDatePicker,
      };
    }
  };

  const onAlertClick = () => {
    if (alertType === AlertType.PRIMARY || alertType === AlertType.SECONDARY) {
      setIsLetUsCallYouModalVisible(true);
      addHotelLetUsCallYouClickEventToGTM({
        title: title || '',
        hotelName: hotelName || '',
        checkInDate: formatMomentDate(formCheckInDate, GTM_DATE_FORMAT),
        checkOutDate: formatMomentDate(formCheckOutDate, GTM_DATE_FORMAT),
        adultCount: hotelRoomList?.[0]?.adultCount || 0,
        childCount: hotelRoomList?.[0]?.childrenList.length || 0,
        serviceType: 'Hotel',
      });
    }
  };

  const onCloseCallMeModal = () => {
    setIsLetUsCallYouModalVisible(false);
  };

  const onLetUsCallYouFormSendSuccess = () => {
    addHotelLetUsCallYouSuccessEventToGTM({
      title: title || '',
      hotelName: hotelName || '',
      checkInDate: formatMomentDate(formCheckInDate, GTM_DATE_FORMAT),
      checkOutDate: formatMomentDate(formCheckOutDate, GTM_DATE_FORMAT),
      serviceType: 'Hotel',
    });
  };
  const query = useSearchParamsToQuery();

  const isLoyaltyValidated = checkLoyaltyPreviouslyValidated(
    loyalty?.loyaltyType
  );
  if (
    shouldHideLoyalty(
      loyalty?.loyaltyType,
      query.utm_source as string,
      isLoyaltyValidated
    )
  )
    return null;
  return (
    <Wrapper>
      <Col>
        <Title>{title}</Title>
        <Description title={promotionInfoName}>{promotionInfoName}</Description>
        {roomSquareMeter ? (
          <Description light>Oda büyüklüğü: {roomSquareMeter} m2</Description>
        ) : null}

        <ReadMoreDescription
          description={description || ''}
          initialVisibleLength={xl ? 300 : 120}
        />

        {alertMessage ? (
          <WarningWrapper>
            <Alert
              title={alertMessage}
              type={alertType}
              onClick={onAlertClick}
            />
          </WarningWrapper>
        ) : null}

        {warningText ? (
          <WarningWrapper>
            <BorderedLabel
              backgroundColor='veryLightFadedOrange'
              color='fadedOrange'
              borderColor='fadedOrange'
              text={warningText}
            />
          </WarningWrapper>
        ) : null}
        {needConfirmation && !isLoyaltyValidated ? (
          <WarningWrapper>
            <BorderedLabel
              backgroundColor='veryLightFadedOrange'
              color='fadedOrange'
              borderColor='fadedOrange'
              text={`İndirimli fiyatı görmek için ${typeToName(
                loyalty?.loyaltyType
              )} hesabınızı doğrulayın.`}
            />
          </WarningWrapper>
        ) : (
          <DynamicRow alignItems='flex-start' justifyContent='space-between'>
            <div>
              {prices?.price && (status ? isPriceAvailable(status) : null) ? (
                <PriceContainer>
                  {prices?.discount ? (
                    <Discount>{prices.discount}</Discount>
                  ) : null}
                  <Price
                    originalPrice={prices?.originalPrice}
                    price={prices?.price}
                    currency={formatCurrency(prices?.currency)}
                    originalPriceCurrency={formatCurrency(
                      prices.originalCurrency
                    )}
                    isExpedia={isExpedia}
                  />
                  {feeDetails ? <FeeDetails feeDetails={feeDetails} /> : null}
                  <Row alignItems='center' justifyContent='flex-end'>
                    <OrText>ya da</OrText>
                    <MilePriceBadge milePrice={prices?.milePrice} />
                  </Row>
                  {roomInformation ? (
                    <PeopleNightCountWrapper>
                      <PeopleNightCount>{roomInformation}</PeopleNightCount>
                    </PeopleNightCountWrapper>
                  ) : null}
                </PriceContainer>
              ) : null}
              <StyledIconLabel
                roomCard={true}
                color={isRefundable ? 'greenInfoDark' : 'redInfoDark'}
                iconColor={isRefundable ? 'greenInfoDark' : 'redInfoDark'}
                backgroundColor={isRefundable ? 'greenInfo' : 'redInfo'}
                text={isRefundable ? 'İptal Edilebilir' : 'İptal Edilemez'}
                icon={isRefundable ? InfoIcon : AlertIcon}
                iconLocation={isRefundable ? 'right' : 'left'}
                iconSize={16}
                tooltipText={
                  isRefundable &&
                  TooltipText({
                    cancellationPolicyDetails,
                    isRefundable,
                  }) ? (
                    <TooltipText
                      cancellationPolicyDetails={cancellationPolicyDetails}
                    />
                  ) : null
                }
              />
            </div>
            {status !== HotelAvailabilityStatusType.N_A ? (
              <StyledButton
                isSelected={isSelected}
                onClick={buttonAction(status).function}
              >
                {buttonAction(status).text}
              </StyledButton>
            ) : null}
          </DynamicRow>
        )}
      </Col>

      {isLetUsCallYouModalVisible &&
      id &&
      formCheckInDate &&
      formCheckOutDate ? (
        <LetUsCallYou
          token=''
          onClose={onCloseCallMeModal}
          onFormSendSuccess={onLetUsCallYouFormSendSuccess}
          entityId={id.toString()}
          entityName={hotelName + ' | ' + title + ' Odası ' || ''}
          currency={Currencies.TL}
          price={prices?.price || 0}
          salesItemType={C4CSalesItemType.OTEL_YURT_ICI}
          itemCountryCode={countryCode}
          availabilityStatus={status}
          formCheckInDate={formCheckInDate}
          formCheckOutDate={formCheckOutDate}
          hotelRoomList={hotelRoomList || []}
          alertType={alertType}
        />
      ) : null}
      {showLoginRedirectionModal ? (
        <LoginRedirectModal
          onCancel={() => setShowLoginRedirectionModal(false)}
          loyaltyType={loyalty?.loyaltyType}
        />
      ) : null}
    </Wrapper>
  );
};

export default RoomSelectionCard;
